import React, { useEffect, useRef, useState } from 'react';
import TextField from '@mui/material/TextField';
import { Controller, UseControllerProps } from 'react-hook-form';
import { Paper, List, ListItem } from '@mui/material';

interface ControlledTextAreaProps {
  label: string;
  id: string;
  name: string;
  className?: string;
  variant?: "standard" | "filled" | "outlined";
  disabled?: boolean;
  control: any;
  defaultValue?: any;
  rules?: UseControllerProps['rules'];
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTextChange?: (event: any) => void;
  required?: boolean;
  setValue?: any;
  register?: any;
  maxLength?: number;
  minLength?: number;
  rows?: number;
  hide?: boolean;
  size?: 'small' | 'medium';
}

const WSTextAreaSuggestion: React.FC<ControlledTextAreaProps> = ({
  id,
  label,
  className,
  control,
  name,
  rules,
  variant = "outlined",
  disabled = false,
  defaultValue = '',
  setValue,
  onChange,
  onTextChange = () => {},
  maxLength,
  minLength,
  rows = 4,
  hide = false,
  size = "medium",
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const textFieldRef = useRef<HTMLInputElement | null>(null);

  const placeholders = ["@amount", "@area"];

  const insertPlaceholder = (placeholder: string, field: any, value: string) => {
    const cursor = cursorPos;
    const before = value.substring(0, cursor);
    const after = value.substring(cursor);
    const newVal = before.replace(/@\w*$/, placeholder) + after;

    field.onChange({ target: { name, value: newVal } });
    setShowSuggestions(false);

    if (onTextChange) {
      onTextChange({ target: { name, value: newVal } });
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field, fieldState }) => {
        const value = field.value ?? "";

        return (
          <div style={{ position: "relative" }}>
            <TextField
              id={id}
              fullWidth
              size={size}
              {...field}
              className={hide === true ? 'ws-hidden' : fieldState.invalid ? 'WSError' : className}
              label={label}
              variant={variant}
              disabled={disabled}
              error={fieldState.invalid}
              value={value}
              helperText={fieldState.error ? fieldState.error.message : ''}
              multiline
              rows={rows}
              inputRef={textFieldRef}
              inputProps={{ maxLength, minLength }}
              onChange={(e: any) => {
                const newValue = e.target.value;
                const cursor = e.target.selectionStart || 0;

                // Detect if user just typed '@'
                const wordBeforeCursor = newValue.slice(0, cursor).split(' ').pop();
                setShowSuggestions(wordBeforeCursor?.startsWith('@'));
                setCursorPos(cursor);

                field.onChange({ target: { name, value: newValue } });

                if (onTextChange) {
                  onTextChange({ target: { name, value: newValue } });
                }
              }}
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.875rem' },
                '& .MuiFormHelperText-root': { fontSize: '0.75rem' },
                '& .MuiInputLabel-root': { fontSize: '0.875rem' },
              }}
            />

            {showSuggestions && (
              <Paper
                sx={{
                  position: 'absolute',
                  bottom: 10,
                  left: 10,
                  zIndex: 1000,
                  width: 150,
                  boxShadow: 3,
                }}
              >
                <List dense>
                  {placeholders.map((ph) => (
                    <ListItem
                      key={ph}
                      button
                      onClick={() => insertPlaceholder(ph, field, value)}
                      sx={{
                        fontSize: '0.875rem',
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: '#f5f5f5' },
                      }}
                    >
                      {ph}
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </div>
        );
      }}
    />
  );
};

export default WSTextAreaSuggestion;
